import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  FileText,
  LoaderCircle,
  Send,
  User,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { sendChatMessage } from "../lib/documentSearch/documentSearchClient";
import type { ChatMessage, SourceDocument } from "../lib/documentSearch/types";

type DocumentChatWorkspaceProps = {
  sources: SourceDocument[];
  initialQuery: string;
  relatedSearchLogId: string | null;
  onBack: () => void;
};

export function DocumentChatWorkspace({
  sources,
  initialQuery,
  relatedSearchLogId,
  onBack,
}: DocumentChatWorkspaceProps) {
  const [threadId] = useState(createThreadId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const initialSent = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const sourceDocumentIds = useMemo(
    () => sources.map((source) => source.sourceDocumentId ?? source.id),
    [sources],
  );
  const largeSources = sources.filter((source) => source.chatSummary?.isLarge);

  useEffect(() => {
    if (initialSent.current) {
      return;
    }
    initialSent.current = true;
    void submitMessage(initialQuery.trim() || "Summarise the selected documents and their relevance.", []);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  async function submitMessage(content: string, history = messages) {
    const message = content.trim();
    if (!message || loading) {
      return;
    }
    const userMessage: ChatMessage = {
      id: createMessageId(),
      role: "user",
      content: message,
    };
    setMessages([...history, userMessage]);
    setDraft("");
    setError("");
    setLoading(true);
    try {
      const requestHistory = history.slice(-20);
      const response = await sendChatMessage({
        threadId,
        message,
        history: requestHistory.map(({ role, content: priorContent }) => ({
          role,
          content: priorContent,
        })),
        sourceDocumentIds,
        relatedSearchLogId: relatedSearchLogId ?? undefined,
      });
      const assistantMessage: ChatMessage = {
        id: response.responseId || createMessageId(),
        role: "assistant",
        content: response.answer,
      };
      setMessages((current) => [...current, assistantMessage]);
    } catch (chatError) {
      setError(chatError instanceof Error ? chatError.message : "Unable to continue this chat");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="chat-workspace">
      <header className="chat-workspace__header">
        <button className="secondary-button" type="button" onClick={onBack}>
          <ArrowLeft size={17} aria-hidden="true" />
          <span>Back to results</span>
        </button>
        <div className="chat-workspace__heading">
          <p className="section-kicker">MFMA document chat</p>
          <h2>Ask across {sources.length} selected source{sources.length === 1 ? "" : "s"}</h2>
        </div>
      </header>

      <section className="chat-source-strip" aria-label="Selected chat sources">
        {sources.map((source, index) => (
          <div className="chat-source-chip" key={source.sourceDocumentId ?? source.id}>
            <FileText size={16} aria-hidden="true" />
            <span>
              <strong>Source {index + 1}</strong>
              {source.title}
            </span>
            {source.chatSummary?.isLarge ? (
              <span
                className="large-source-badge"
                title="This source exceeds the large-document size threshold"
              >
                <AlertTriangle size={13} aria-hidden="true" />
                Large
              </span>
            ) : null}
          </div>
        ))}
      </section>

      {largeSources.length > 0 ? (
        <div className="chat-large-warning" role="status">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>
            {largeSources.length} selected source{largeSources.length === 1 ? " is" : "s are"} very
            large. The chat uses their generated briefs, so check the originals for omitted detail.
          </span>
        </div>
      ) : null}

      <section className="chat-transcript" aria-live="polite">
        {messages.map((message) => (
          <article className={`chat-message chat-message--${message.role}`} key={message.id}>
            <div className="chat-message__avatar" aria-hidden="true">
              {message.role === "assistant" ? <Bot size={18} /> : <User size={18} />}
            </div>
            <div className="chat-message__body">
              <strong>{message.role === "assistant" ? "MFMA source assistant" : "You"}</strong>
              {message.role === "assistant" ? (
                <div className="chat-message__markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
                    {message.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="chat-message__plain-text">{message.content}</p>
              )}
            </div>
          </article>
        ))}
        {loading ? (
          <article className="chat-message chat-message--assistant chat-message--loading">
            <div className="chat-message__avatar" aria-hidden="true">
              <LoaderCircle className="spin" size={18} />
            </div>
            <div className="chat-message__body">
              <strong>MFMA source assistant</strong>
              <p>Reading the selected document briefs…</p>
            </div>
          </article>
        ) : null}
        <div ref={messagesEndRef} />
      </section>

      {error ? <p className="form-error chat-error">{error}</p> : null}

      <form
        className="chat-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void submitMessage(draft);
        }}
      >
        <label>
          <span className="sr-only">Message</span>
          <textarea
            rows={3}
            value={draft}
            maxLength={8_000}
            placeholder="Ask a follow-up about the selected MFMA sources…"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void submitMessage(draft);
              }
            }}
          />
        </label>
        <button className="primary-button" type="submit" disabled={loading || !draft.trim()}>
          {loading ? (
            <LoaderCircle className="spin" size={18} aria-hidden="true" />
          ) : (
            <Send size={18} aria-hidden="true" />
          )}
          <span>Send</span>
        </button>
      </form>
    </main>
  );
}

function createThreadId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `thread-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createMessageId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `message-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
